import React, {useState, useEffect, useMemo} from 'react';
import {useNavigate} from 'react-router-dom';
import axios from 'axios';
import {useUser} from './UserContext';
import styles from '../designs/HistoryPage.module.css';
import {isDemoModeEnabled} from '../utils/demoAccess';

const toLocalDateKey = (date = new Date()) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const getBetDateKey = (bet) => (
    bet.date ||
    bet.match_date ||
    (bet.created_at && String(bet.created_at).split(/[ T]/)[0]) ||
    'Без дати'
);

const formatDateLabel = (dateValue) => {
    if (!dateValue || dateValue === 'Без дати') return 'Без дати';

    const parsed = new Date(`${dateValue}T12:00:00`);
    if (Number.isNaN(parsed.getTime())) return String(dateValue);

    const today = new Date();
    const todayKey = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
    const targetKey = new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate()).getTime();
    const diffDays = Math.round((todayKey - targetKey) / 86400000);

    if (diffDays === 0) return 'Сьогодні';
    if (diffDays === 1) return 'Вчора';

    return parsed.toLocaleDateString('uk-UA', {
        day: 'numeric',
        month: 'long',
        year: parsed.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
    });
};

const getBetTeams = (bet) => ({
    homeTeam: bet.home_team || bet.homeTeam || (bet.match && bet.match.split(' vs ')[0]) || 'Home',
    awayTeam: bet.away_team || bet.awayTeam || (bet.match && bet.match.split(' vs ')[1]) || 'Away',
});

const isLiveBet = (bet) => (
    String(bet.bet_type || bet.type || '').toLowerCase() === 'live' ||
    !!bet.live_top3_bets ||
    !!bet.live_opinion
);

const getBetTypeLabel = (bet) => (isLiveBet(bet) ? 'LIVE' : 'PREMATCH');

const getLeagueLabel = (bet) => {
    const leagueName = String(bet.league_name || bet.league || '').trim();
    const country = String(bet.country || '').trim();
    if (leagueName && country) return `${leagueName} · ${country}`;
    if (leagueName) return leagueName;
    if (country && country !== 'UNKNOWN') return country;
    return 'Ліга невідома';
};

const getLeagueKey = (bet) => {
    const leagueId = String(bet.league_id || '').trim();
    if (leagueId) return `id:${leagueId}`;
    return `name:${getLeagueLabel(bet).toLocaleLowerCase('uk-UA')}`;
};

const getCenterLabel = (bet) => {
    const scoreText = String(bet.score || '').trim();
    if (scoreText && scoreText !== 'TBD') return scoreText;
    if (bet.time) return bet.time;
    return 'VS';
};

const getBetChoiceText = (bet) => (
    bet.live_top3_bets ||
    bet.top3_bets ||
    bet.bet_text ||
    bet.bet ||
    ''
);

const getBetOpinionText = (bet) => (
    bet.live_opinion ||
    bet.opinion ||
    ''
);

function HistoryPage() {
    const navigate = useNavigate();

    useEffect(() => {
        if (isDemoModeEnabled()) {
            navigate('/access-request', {replace: true});
        }
    }, [navigate]);
    const {user, loading, error} = useUser();
    const [bets, setBets] = useState([]);
    const [betsLoading, setBetsLoading] = useState(true);
    const [betsError, setBetsError] = useState(null);
    const [expandedCards, setExpandedCards] = useState({});
    const [searchQuery, setSearchQuery] = useState('');
    const [betTypeFilter, setBetTypeFilter] = useState('all');
    const [leagueFilter, setLeagueFilter] = useState('all');
    const [copiedCard, setCopiedCard] = useState('');

    const fetchBets = async (telegramId) => {
        setBetsLoading(true);
        setBetsError(null);
        try {
            const API_BASE = process.env.REACT_APP_API_BASE || '/api';
            const response = await axios.get(`${API_BASE}/user/${telegramId}/bets`, {
                headers: {
                    'Cache-Control': 'no-cache, no-store, must-revalidate',
                    Pragma: 'no-cache',
                    Expires: '0',
                    'X-Telegram-Init-Data': window.Telegram?.WebApp?.initData || '',
                },
            });
            setBets(response.data.bets || []);
        } catch (err) {
            console.error('Error fetching bets:', err.response ? err.response.data : err.message);
            setBetsError('Не вдалося завантажити історію ставок.');
        } finally {
            setBetsLoading(false);
        }
    };

    useEffect(() => {
        if (user?.telegram_id) fetchBets(user.telegram_id);
    }, [user]);

    const stats = useMemo(() => {
        const todayKey = toLocalDateKey();
        const weekStart = new Date();
        weekStart.setHours(0, 0, 0, 0);
        weekStart.setDate(weekStart.getDate() - 6);

        const thisWeek = bets.filter((bet) => {
            const parsed = new Date(`${getBetDateKey(bet)}T12:00:00`);
            return !Number.isNaN(parsed.getTime()) && parsed >= weekStart;
        }).length;

        return {
            total: bets.length,
            today: bets.filter((bet) => getBetDateKey(bet) === todayKey).length,
            thisWeek,
            live: bets.filter(isLiveBet).length,
        };
    }, [bets]);

    const leagueOptions = useMemo(() => {
        const leagues = new Map();
        bets.forEach((bet) => {
            const key = getLeagueKey(bet);
            if (!leagues.has(key)) {
                leagues.set(key, {
                    key,
                    label: getLeagueLabel(bet),
                    count: 0,
                });
            }
            leagues.get(key).count += 1;
        });
        return Array.from(leagues.values())
            .sort((a, b) => {
                if (a.label === 'Ліга невідома') return 1;
                if (b.label === 'Ліга невідома') return -1;
                return a.label.localeCompare(b.label, 'uk-UA');
            });
    }, [bets]);

    const filteredBets = useMemo(() => {
        const query = searchQuery.trim().toLocaleLowerCase('uk-UA');

        return bets.filter((bet) => {
            if (betTypeFilter === 'live' && !isLiveBet(bet)) return false;
            if (betTypeFilter === 'prematch' && isLiveBet(bet)) return false;
            if (leagueFilter !== 'all' && getLeagueKey(bet) !== leagueFilter) return false;

            if (!query) return true;
            const {homeTeam, awayTeam} = getBetTeams(bet);
            const searchableText = [
                homeTeam,
                awayTeam,
                bet.match,
                getBetChoiceText(bet),
                getBetOpinionText(bet),
                getLeagueLabel(bet),
            ]
                .filter(Boolean)
                .join(' ')
                .toLocaleLowerCase('uk-UA');
            return searchableText.includes(query);
        });
    }, [bets, searchQuery, betTypeFilter, leagueFilter]);

    const resetFilters = () => {
        setSearchQuery('');
        setBetTypeFilter('all');
        setLeagueFilter('all');
    };

    const groupedBets = useMemo(() => {
        const reversed = [...filteredBets].reverse();
        const groups = new Map();

        reversed.forEach((bet) => {
            const key = getBetDateKey(bet);
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(bet);
        });

        return Array.from(groups.entries()).map(([dateKey, items]) => ({
            dateKey,
            label: formatDateLabel(dateKey),
            items,
        }));
    }, [filteredBets]);

    const toggleExpandedCard = (cardKey) => {
        setExpandedCards((previous) => ({
            ...previous,
            [cardKey]: !previous[cardKey],
        }));
        window.Telegram?.WebApp?.HapticFeedback?.selectionChanged?.();
    };

    const copyBet = async (bet, cardKey) => {
        const {homeTeam, awayTeam} = getBetTeams(bet);
        const betChoiceText = getBetChoiceText(bet);
        const betOpinionText = getBetOpinionText(bet);
        const text = [
            `${homeTeam} — ${awayTeam}`,
            betChoiceText ? `Вибір:\n${betChoiceText}` : '',
            betOpinionText ? `Думка:\n${betOpinionText}` : '',
        ].filter(Boolean).join('\n\n');

        try {
            await navigator.clipboard.writeText(text);
            setCopiedCard(cardKey);
            window.Telegram?.WebApp?.HapticFeedback?.notificationOccurred?.('success');
            window.setTimeout(() => setCopiedCard(''), 1600);
        } catch {
            setCopiedCard('error');
            window.setTimeout(() => setCopiedCard(''), 1600);
        }
    };

    const renderPageState = (title, text, actionLabel, action) => (
        <div className={styles.container}>
            <div className={styles.animatedGradient}/>
            <div className={styles.stateCard}>
                <div className={styles.stateMark}>G</div>
                <h2>{title}</h2>
                <p>{text}</p>
                {actionLabel && <button onClick={action}>{actionLabel}</button>}
            </div>
        </div>
    );

    if (loading) return renderPageState('Завантажуємо профіль', 'Ще мить — відкриваємо твій архів рішень.');
    if (error) return renderPageState('Не вдалося відкрити історію', error, 'На головну', () => navigate('/'));
    if (betsLoading) return renderPageState('Збираємо історію', 'Готуємо останні ставки та аналітику.');
    if (betsError) return renderPageState('Історія недоступна', betsError, 'Спробувати ще раз', () => fetchBets(user.telegram_id));

    return (
        <div className={styles.container}>
            <div className={styles.animatedGradient}/>

            <div className={styles.topBar}>
                <button onClick={() => navigate(-1)} className={styles.backButton}>Назад</button>
                <span className={styles.archiveCode}>G / ARCHIVE</span>
            </div>

            <header className={styles.pageHero}>
                <div className={styles.eyebrow}>Твій архів рішень</div>
                <h1 className={styles.pageTitle}>ІСТОРІЯ <span>ГРИ</span></h1>
                <p className={styles.pageSubtitle}>Усі збережені ставки, думки та матчі в одному місці.</p>
            </header>

            <section className={styles.statsGrid} aria-label="Статистика історії">
                <div className={styles.statCard}>
                    <span>Усього</span>
                    <strong>{stats.total}</strong>
                    <small>ставок в архіві</small>
                </div>
                <div className={styles.statCard}>
                    <span>Сьогодні</span>
                    <strong>{stats.today}</strong>
                    <small>нових рішень</small>
                </div>
                <div className={styles.statCard}>
                    <span>7 днів</span>
                    <strong>{stats.thisWeek}</strong>
                    <small>активність</small>
                </div>
                <div className={styles.statCard}>
                    <span>LIVE</span>
                    <strong>{stats.live}</strong>
                    <small>лайв ставок</small>
                </div>
            </section>

            <section className={styles.historyTools}>
                <label className={styles.searchBox}>
                    <span aria-hidden="true">⌕</span>
                    <input
                        value={searchQuery}
                        onChange={(event) => setSearchQuery(event.target.value)}
                        placeholder="Знайти команду або ставку"
                        aria-label="Пошук в історії"
                    />
                    {searchQuery && (
                        <button type="button" onClick={() => setSearchQuery('')} aria-label="Очистити пошук">×</button>
                    )}
                </label>

                <div className={styles.filterPanel}>
                    <div className={styles.filterBlock}>
                        <span>Тип ставки</span>
                        <div className={styles.segmentedControl}>
                            {[
                                ['all', 'Усі'],
                                ['prematch', 'Prematch'],
                                ['live', 'LIVE'],
                            ].map(([id, label]) => (
                                <button
                                    key={id}
                                    type="button"
                                    onClick={() => setBetTypeFilter(id)}
                                    className={betTypeFilter === id ? styles.filterActive : ''}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className={styles.filterBlock}>
                        <span>Ліга</span>
                        <select
                            value={leagueFilter}
                            onChange={(event) => setLeagueFilter(event.target.value)}
                            aria-label="Фільтр за лігою"
                        >
                            <option value="all">Усі ліги</option>
                            {leagueOptions.map((league) => (
                                <option key={league.key} value={league.key}>
                                    {league.label} ({league.count})
                                </option>
                            ))}
                        </select>
                    </label>

                    <div className={styles.filterSummary}>
                        <span className={styles.resultCount}>{filteredBets.length} знайдено</span>
                        {(searchQuery || betTypeFilter !== 'all' || leagueFilter !== 'all') && (
                            <button type="button" onClick={resetFilters}>Скинути</button>
                        )}
                    </div>
                </div>
            </section>

            <main className={styles.betsContainer}>
                {groupedBets.length > 0 ? groupedBets.map((group, groupIndex) => (
                    <section key={group.dateKey} className={styles.dateSection}>
                        <div className={styles.dateHeader}>
                            <span>{group.label}</span>
                            <small>{group.items.length}</small>
                        </div>

                        <div className={styles.dateSectionList}>
                            {group.items.map((bet, idx) => {
                                const {homeTeam, awayTeam} = getBetTeams(bet);
                                const homeLogo = bet.home_logo || bet.homeTeamLogo || bet.home_team_logo || null;
                                const awayLogo = bet.away_logo || bet.awayTeamLogo || bet.away_team_logo || null;
                                const cardKey = `${group.dateKey}_${idx}_${homeTeam}_${awayTeam}`;
                                const isExpanded = !!expandedCards[cardKey];
                                const betChoiceText = getBetChoiceText(bet);
                                const betOpinionText = getBetOpinionText(bet);

                                return (
                                    <article
                                        key={cardKey}
                                        className={styles.betCardShell}
                                        style={{'--card-order': Math.min(groupIndex * 2 + idx, 12)}}
                                    >
                                        <button
                                            type="button"
                                            className={`${styles.betButton} ${isExpanded ? styles.betButtonExpanded : ''}`}
                                            onClick={() => toggleExpandedCard(cardKey)}
                                            aria-expanded={isExpanded}
                                        >
                                            <div className={styles.cardTopLine}>
                                                <span className={`${styles.cardType} ${isLiveBet(bet) ? styles.cardTypeLive : ''}`}>
                                                    {getBetTypeLabel(bet)}
                                                </span>
                                                <span className={styles.cardLeague}>{getLeagueLabel(bet)}</span>
                                                <span className={styles.cardTime}>{bet.time || 'Архів'}</span>
                                            </div>

                                            <div className={styles.betMainRow}>
                                                <div className={styles.teamSide}>
                                                    <div className={styles.teamLogoWrap}>
                                                        {homeLogo ? <img src={homeLogo} alt=""/> : <span>{homeTeam.charAt(0)}</span>}
                                                    </div>
                                                    <span className={styles.teamName}>{homeTeam}</span>
                                                </div>

                                                <span className={styles.vsLabel}>{getCenterLabel(bet)}</span>

                                                <div className={styles.teamSide}>
                                                    <div className={styles.teamLogoWrap}>
                                                        {awayLogo ? <img src={awayLogo} alt=""/> : <span>{awayTeam.charAt(0)}</span>}
                                                    </div>
                                                    <span className={styles.teamName}>{awayTeam}</span>
                                                </div>
                                            </div>

                                            {betChoiceText && (
                                                <div className={styles.betPreview}>
                                                    <span>Вибір</span>
                                                    <p>{betChoiceText}</p>
                                                </div>
                                            )}

                                            <div className={styles.expandHint}>
                                                <span>{isExpanded ? 'Сховати деталі' : 'Відкрити деталі'}</span>
                                                <b>{isExpanded ? '−' : '+'}</b>
                                            </div>
                                        </button>

                                        {isExpanded && (
                                            <div className={styles.expandedContent}>
                                                {betChoiceText && (
                                                    <div className={styles.expandedBlock}>
                                                        <div className={styles.expandedTitle}><span/> Вибір</div>
                                                        <div className={styles.expandedText}>{betChoiceText}</div>
                                                    </div>
                                                )}

                                                {betOpinionText && (
                                                    <div className={styles.expandedBlock}>
                                                        <div className={styles.expandedTitle}><span/> Думка</div>
                                                        <div className={styles.expandedText}>{betOpinionText}</div>
                                                    </div>
                                                )}

                                                <div className={styles.expandedFooter}>
                                                    <span>{bet.created_at ? `Збережено: ${bet.created_at}` : `Матч: ${group.label}`}</span>
                                                    <button type="button" onClick={() => copyBet(bet, cardKey)}>
                                                        {copiedCard === cardKey ? 'Скопійовано ✓' : copiedCard === 'error' ? 'Помилка' : 'Копіювати ставку'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </article>
                                );
                            })}
                        </div>
                    </section>
                )) : (
                    <div className={styles.emptyState}>
                        <div className={styles.emptyIcon}>⌕</div>
                        <div className={styles.emptyStateTitle}>
                            {bets.length ? 'Нічого не знайдено' : 'Архів поки порожній'}
                        </div>
                        <div className={styles.emptyStateText}>
                            {bets.length
                                ? 'Спробуй змінити лігу, тип ставки або пошук.'
                                : 'Отримай першу ставку — вона автоматично з’явиться тут.'}
                        </div>
                        {bets.length ? (
                            <button onClick={resetFilters}>Скинути фільтри</button>
                        ) : (
                            <button onClick={() => navigate('/football')}>Перейти до матчів</button>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

export default HistoryPage;
